import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../fragments/PageAuthorization";
import {PentaSplit} from "../../components/common/PentaSplit";
import {
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Stack, TextField,
	Typography
} from "@mui/material";
import {Link} from "react-router-dom";
import {GraphQlDataGrid} from "../../components/common/grid/GraphQlDataGrid";
import {graphQLServices} from "../../services/GraphQLServices";
import {EXISTS_MODULE, GET_MODULES, PUT_MODULE} from "./Queries";
import {formatDateTimeShort} from "../../global/GlobalState";
import {textColumn, tooltipColumn} from "../../components/common/grid/Columns";
import {translationsEditorTargetStage} from "./TranslationsOverview";
import {linkModuleDetailsFor} from "./ModuleDetails";
import {GridColDef, GridPagination} from "@mui/x-data-grid";
import {Module} from "./models/Module";
import {cloneMutationData} from "../products/Utils";
import {ProcessButton} from "../../components/common/input/ProcessButton";
import {translateFunction} from "../../localization/TranslationService";

const translate = translateFunction("SureBasePortalTranslations");

export const ModulesOverview = pageAuthorizedByGroup(function () {
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Open"),
			renderCell: (params) => (
				<Link to={linkModuleDetailsFor(params.row.id)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
			hideable: false,
		},
		{
			field: "name",
			headerName: translate("Name"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "creationDateTime",
			headerName: translate("Creation Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1
		}
		/* todo access */
	];
	
	const [resetKey, setResetKey] = useState(0);
	const updateGrid = () => setResetKey(resetKey + 1);
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/translations_setup">{translate("Translation Setup")}</Link>
			<Typography color="text.primary">{translate("Modules")}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<GraphQlDataGrid
				rows={[]}
				query={GET_MODULES}
				graphFunction={"modules"}
				client={graphQLServices.translationsClient}
				rowsPerPageOptions={[50, 500, 1000]}
				initialPageSize={50}
				columns={columns}
				getRowId={row => row.id}
				defaultSortModel={[
					{
						field: "name",
						sort: "asc"
					}
				]}
				queryVariables={{stage: translationsEditorTargetStage, resetKey}}
				initialState={{
					columns: {
						columnVisibilityModel: {
							creationDateTime: false
						},
					},
				}}
				slots={{
					footer: CustomFooterFactory(updateGrid)
				}}
			/>
		</Paper>}
	/>;
}, ["Admin", "Translations"]);

async function moduleExists(item: Module): Promise<boolean> {
	
	const existsItems = await graphQLServices.translationsClient.query(
		{
			query: EXISTS_MODULE,
			variables: {
				stage: translationsEditorTargetStage,
				name: item.name,
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.modules.nodes;
	return nodes.length > 0;
}


async function saveChanges(module: Module) {
	await graphQLServices.translationsClient.mutate({
		mutation: PUT_MODULE,
		variables: {
			stage: translationsEditorTargetStage,
			module: cloneMutationData(module),
		}
	});
}

function CustomFooterFactory(updateGrid: () => void) {
	return function CustomFooter(props: any) {
		const [open, setOpen] = useState(false);
		
		const [name, setName] = useState("");
		
		const handleClickOpen = () => setOpen(true);
		const handleClose = () => setOpen(false);
		
		const handleCreate = async () => {
			const id = crypto.randomUUID();
			const newItem = {id, name} as Module;
			await moduleExists(newItem).then(async (x) => {
				if (x) {
					throw new Error(`Item already exists`);
				}
				
				await saveChanges(newItem);
				setOpen(false);
				updateGrid();
			});
		};
		
		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
			padding: "0 10px"
		}}>
			<Stack direction={"row"} spacing={2} alignItems={"center"}>
				<Button
					variant={"contained"}
					onClick={handleClickOpen}
				>{translate("Add")}</Button>
			</Stack>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{translate("New Module")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Please provide the following information.")}
					</DialogContentText>
					<form>
						<TextField
							margin="dense"
							label={translate("Name")}
							fullWidth
							variant="standard"
							required
							value={name}
							onChange={event => {
								setName(event.target.value);
							}}
						/>
						<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
							<Button onClick={handleClose}>{translate("Cancel")}</Button>
							<div style={{flexGrow: 0, padding: "10px"}}>
								<ProcessButton
									defaultText={translate("Create")}
									inProgressText={translate("Creating")}
									completedText={translate("Created")}
									resetKeys={[name]}
									variant={"outlined"}
									onClick={handleCreate}/>
							</div>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>
			<GridPagination {...props} labelRowsPerPage={translate("Rows per page:")}/>
		</Stack>;
	};
}
